/**
 * Pro license core (spec §7.5, SP2).
 *
 * The local key check uses the shape `CFRG-XXXX-XXXX-CCCC` where the final group
 * is a base-36 checksum of the rest. Everything is architected behind two
 * interfaces so a remote validation call replaces the stub without touching call
 * sites:
 *
 * - {@link LicenseValidator} — turns a key string into a {@link LicenseStatus}.
 *   {@link stubValidator} is the local implementation; a remote validator can be
 *   swapped in via {@link createLicense}.
 * - {@link LicenseStore} — where the activated key is persisted.
 *   {@link localStorageStore} is the browser default; {@link memoryStore} backs
 *   tests and non-browser callers.
 *
 * TRUST MODEL: the local stub is fully client-side and trivially mintable — the
 * checksum lives in the shipped bundle. It is therefore a dev / offline / e2e
 * affordance ONLY. The hardcoded dev-key bypass was removed (cue-forge-sdv.1),
 * and on a real production build the app composes the License with a
 * reject-everything local validator (`localValidator(false)` in the app layer)
 * so Pro can only be granted by the remote (Lemon Squeezy) validator. Gating is
 * still not a hard security boundary — a determined user can poke the cached
 * status in DevTools — but no published key or in-bundle checksum unlocks Pro.
 */

/** Subscription lifecycle state reported by a (remote) validator, when known. */
export type LicenseState = 'active' | 'expired' | 'cancelled';

export interface LicenseStatus {
  /**
   * Whether Pro is granted **right now**. For a perpetual stub key this is the
   * checksum result; for a subscription {@link createLicense} folds the offline
   * grace window into it, so a recently-expired status reads `valid: true` until
   * the grace elapses. UI reads this directly.
   */
  valid: boolean;
  tier: 'pro';
  /** Last-known subscription state (remote validators set it; the stub omits it). */
  state?: LicenseState;
  /** Subscription expiry, epoch ms (absent ⇒ perpetual, e.g. stub keys). */
  expiresAt?: number;
  /** Epoch ms of the last successful (remote) validation. */
  lastValidatedAt?: number;
}

/** Validates a license key synchronously (the local stub / cache seed). */
export interface LicenseValidator {
  validate(key: string): LicenseStatus;
}

/** Remote, asynchronous validator used by {@link License.refresh}. */
export interface RemoteLicenseValidator {
  validate(key: string): Promise<LicenseStatus>;
}

/**
 * Where an activated key and its last-known status are persisted. The status
 * methods are optional: stores without them simply don't survive a reload (so
 * offline grace resets), which is fine for tests.
 */
export interface LicenseStore {
  get(): string | null;
  set(key: string): void;
  clear(): void;
  /** Read the cached status snapshot (offline grace across reloads). */
  getStatus?(): LicenseStatus | null;
  /** Persist (or clear, with null) the cached status snapshot. */
  setStatus?(status: LicenseStatus | null): void;
}

const PREFIX = 'CFRG';
const BODY_RE = /^[0-9A-Z]{8}$/;
const KEY_RE = /^CFRG-([0-9A-Z]{4})-([0-9A-Z]{4})-([0-9A-Z]{4})$/;
const ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/** Deterministic 4-char base-36 checksum over the key's payload. */
function checksum(payload: string): string {
  let h = 0;
  for (let i = 0; i < payload.length; i++) {
    h = (h * 31 + payload.charCodeAt(i)) >>> 0;
  }
  let out = '';
  for (let i = 0; i < 4; i++) {
    out = ALPHABET[h % 36] + out;
    h = Math.floor(h / 36);
  }
  return out;
}

/**
 * Build a valid key from an 8-char alphanumeric body — the stub counterpart to
 * the validator, used to mint keys (e.g. fixtures, tests). A real issuer would
 * live server-side; this is the local seam.
 */
export function signKey(body: string): string {
  if (!BODY_RE.test(body)) {
    throw new Error(`signKey expects 8 uppercase alphanumeric chars, got: ${body}`);
  }
  const g1 = body.slice(0, 4);
  const g2 = body.slice(4, 8);
  const payload = `${PREFIX}-${g1}-${g2}`;
  return `${payload}-${checksum(payload)}`;
}

/**
 * A normally-signed key the stub validator accepts, used by unit tests and the
 * e2e suite to exercise the Pro flow. It is **not** a magic-string bypass (that
 * was removed in cue-forge-sdv.1): it validates only because its checksum is
 * correct, exactly like any other stub key, and the production build does not
 * trust the stub validator at all (see {@link localValidator} in the app layer).
 */
export const DEV_KEY = signKey('DEV00000');

/** Normalise raw user input: trim and uppercase. */
function normalise(key: string): string {
  return key.trim().toUpperCase();
}

/** The MVP local validator: key format + base-36 checksum (no magic bypass). */
export function validateKey(key: string): LicenseStatus {
  const normalised = normalise(key);
  const match = KEY_RE.exec(normalised);
  if (!match) return { valid: false, tier: 'pro' };
  const [, g1, g2, given] = match;
  const expected = checksum(`${PREFIX}-${g1}-${g2}`);
  return { valid: given === expected, tier: 'pro' };
}

/** The local validator as a {@link LicenseValidator}. */
export const stubValidator: LicenseValidator = { validate: validateKey };

/** Activated-license accessor over a {@link LicenseStore} + {@link LicenseValidator}. */
export interface License {
  /** Validate a key with the sync validator; persist it on success. */
  activate(key: string): LicenseStatus;
  /**
   * Activate a key, validating real keys against the remote validator. Stub /
   * DEV keys still activate synchronously (no network); a key the sync stub
   * rejects (e.g. a Lemon Squeezy UUID) is validated remotely and persisted only
   * when the remote grants Pro, so {@link License.refresh} can later re-check it.
   * Falls back to {@link License.activate} when no remote validator is configured.
   */
  activateRemote(key: string): Promise<LicenseStatus>;
  /** Forget the stored key and status. */
  deactivate(): void;
  /** Cached, grace-adjusted status (a render-safe synchronous read). */
  current(): LicenseStatus;
  /** Convenience: is Pro currently granted (cache + grace)? */
  isPro(): boolean;
  /**
   * Re-validate the stored key against the remote validator (or the sync one if
   * no remote is configured), updating and persisting the cached status.
   */
  refresh(): Promise<LicenseStatus>;
}

/** Default offline grace: a subscription keeps Pro for 14 days past expiry. */
export const DEFAULT_GRACE_MS = 14 * 24 * 60 * 60 * 1000;

export interface LicenseOptions {
  /** Clock injection for grace logic and `lastValidatedAt` (default Date.now). */
  now?: () => number;
  /** Offline grace window past expiry, in ms (default {@link DEFAULT_GRACE_MS}). */
  graceMs?: number;
  /** Remote validator for {@link License.refresh}; falls back to the sync validator. */
  remote?: RemoteLicenseValidator;
}

const FREE: LicenseStatus = { valid: false, tier: 'pro' };

export function createLicense(
  store: LicenseStore,
  validator: LicenseValidator = stubValidator,
  opts: LicenseOptions = {},
): License {
  const now = opts.now ?? (() => Date.now());
  const graceMs = opts.graceMs ?? DEFAULT_GRACE_MS;

  // Seed the cache: a persisted snapshot (offline grace across reloads) wins;
  // otherwise synchronously validate a stored bare key; otherwise free.
  let cached: LicenseStatus = (() => {
    const snapshot = store.getStatus?.();
    if (snapshot) return snapshot;
    const key = store.get();
    return key ? validator.validate(key) : FREE;
  })();

  /** Whether the cached status grants Pro right now, folding in offline grace. */
  function grantsPro(s: LicenseStatus): boolean {
    if (s.state === 'cancelled') return false;
    if (s.expiresAt !== undefined) return now() <= s.expiresAt + graceMs;
    return s.valid;
  }

  /** Cached status with `valid` reflecting the current grace decision. */
  function effective(s: LicenseStatus): LicenseStatus {
    return { ...s, valid: grantsPro(s) };
  }

  function setCached(status: LicenseStatus): void {
    cached = status;
    store.setStatus?.(status);
  }

  function activate(key: string): LicenseStatus {
    const status = validator.validate(key);
    if (status.valid) {
      store.set(normalise(key));
      setCached(status);
    }
    return effective(status);
  }

  async function activateRemote(key: string): Promise<LicenseStatus> {
    // Stub / DEV keys activate synchronously — never hit the network.
    const sync = activate(key);
    if (sync.valid || !opts.remote) return sync;
    // A key the stub rejects (e.g. a Lemon Squeezy UUID): validate remotely and
    // persist only if the remote grants Pro, so refresh() can re-check / re-lock.
    // A network/server failure REJECTS (nothing activated or persisted) rather
    // than resolving invalid: callers must be able to tell "couldn't reach the
    // license server" from "the server rejected this key" (cue-forge-drt.19).
    const status = await opts.remote.validate(key);
    if (grantsPro(status)) {
      store.set(normalise(key));
      setCached({ ...status, lastValidatedAt: now() });
      return effective(cached);
    }
    return effective(status);
  }

  return {
    activate,
    activateRemote,
    deactivate() {
      store.clear();
      store.setStatus?.(null);
      cached = FREE;
    },
    current() {
      return effective(cached);
    },
    isPro() {
      return grantsPro(cached);
    },
    async refresh() {
      const key = store.get();
      if (!key) {
        setCached(FREE);
        return effective(cached);
      }
      // Keys the local validator accepts stay local — a configured remote can't
      // invalidate them. In production the local validator rejects everything
      // (localValidator(false)), so every key — including real Lemon Squeezy
      // UUIDs — is re-checked against the remote; only dev/e2e builds short-
      // circuit here, which keeps the offline pro-gate green.
      const local = validator.validate(key);
      const status = opts.remote && !local.valid ? await opts.remote.validate(key) : local;
      setCached({ ...status, lastValidatedAt: now() });
      return effective(cached);
    },
  };
}

/** In-memory {@link LicenseStore} for tests and non-browser callers. */
export function memoryStore(initial: string | null = null): LicenseStore {
  let value = initial;
  let status: LicenseStatus | null = null;
  return {
    get: () => value,
    set: (key) => {
      value = key;
    },
    clear: () => {
      value = null;
    },
    getStatus: () => status,
    setStatus: (s) => {
      status = s;
    },
  };
}

const STORAGE_KEY = 'cueforge.license';
const STATUS_KEY = 'cueforge.license.status';

/**
 * Browser {@link LicenseStore} backed by `localStorage`, guarded so importing
 * the module in a non-browser context (the framework-free core test run) is
 * safe. Falls back to a no-op when `localStorage` is unavailable.
 */
export const localStorageStore: LicenseStore = {
  get() {
    try {
      return globalThis.localStorage?.getItem(STORAGE_KEY) ?? null;
    } catch {
      return null;
    }
  },
  set(key) {
    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, key);
    } catch {
      /* storage unavailable — non-persistent session */
    }
  },
  clear() {
    try {
      globalThis.localStorage?.removeItem(STORAGE_KEY);
    } catch {
      /* storage unavailable */
    }
  },
  getStatus() {
    try {
      const raw = globalThis.localStorage?.getItem(STATUS_KEY);
      return raw ? (JSON.parse(raw) as LicenseStatus) : null;
    } catch {
      return null;
    }
  },
  setStatus(status) {
    try {
      if (status) globalThis.localStorage?.setItem(STATUS_KEY, JSON.stringify(status));
      else globalThis.localStorage?.removeItem(STATUS_KEY);
    } catch {
      /* storage unavailable */
    }
  },
};
